import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle, ArrowClockwise, XCircle } from "@phosphor-icons/react";

export default function PaymentStatus() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const cancelled = params.get("cancelled");
  const [state, setState] = useState({ status: "checking", payment_status: null, amount_total: 0, currency: "usd" });
  const [attempts, setAttempts] = useState(0);
  const timer = useRef(null);

  useEffect(() => {
    if (!sessionId) { setState({ status: "error", payment_status: null }); return; }
    if (cancelled) { setState({ status: "cancelled", payment_status: "cancelled" }); return; }

    const poll = async (n) => {
      if (n >= 10) { setState((s) => ({ ...s, status: "timeout" })); return; }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setState({ ...data, status: "paid" }); return; }
        if (data.status === "expired") { setState({ ...data, status: "expired" }); return; }
        setAttempts(n + 1);
        timer.current = setTimeout(() => poll(n + 1), 2000);
      } catch (e) {
        setState((s) => ({ ...s, status: "error", error: formatApiErrorDetail(e.response?.data?.detail) }));
      }
    };
    poll(0);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [sessionId, cancelled]);

  const paid = state.status === "paid";
  const isCancelled = state.status === "cancelled";
  const isError = state.status === "error" || state.status === "timeout" || state.status === "expired";
  const purpose = state.purpose ?? state.metadata?.purpose;
  const amountStr = `$${((state.amount_total || 0) / 100).toLocaleString()} ${state.currency?.toUpperCase()}`;

  const paidCopy = () => {
    const monthlyCents = state.monthly_cents ?? parseInt(state.metadata?.monthly_cents || "0", 10);
    const monthlyStr = monthlyCents > 0
      ? `$${(monthlyCents / 100).toLocaleString()} ${state.currency?.toUpperCase()}`
      : null;

    if (purpose === "onboarding") {
      return {
        heading: "You're onboarded.",
        body: monthlyStr
          ? `${amountStr} was charged today — your first month plus a last-month retainer. ${monthlyStr} will be billed automatically on the same date each month going forward. Jonathan will reach out shortly to schedule your first on-site session.`
          : `${amountStr} was charged today — your first month plus a last-month retainer. Your monthly fee will continue to be billed automatically each month going forward. Jonathan will reach out shortly to schedule your first on-site session.`,
      };
    }
    if (purpose === "retainer") {
      return {
        heading: "Retainer received.",
        body: `Your first-month + last-month retainer of ${amountStr} is confirmed — that's step one of your onboarding. Jonathan will reach out shortly to schedule your first on-site session and send your recurring monthly billing link.`,
      };
    }
    if (purpose === "subscription") {
      return {
        heading: "Monthly billing active.",
        body: `Your recurring monthly billing of ${amountStr} is now set up — onboarding complete. Jonathan will be in touch about your session schedule.`,
      };
    }
    return {
      heading: "Payment received.",
      body: `${amountStr} has been confirmed. We'll be in touch shortly with next steps.`,
    };
  };

  return (
    <div className="min-h-screen paper-grain flex items-center justify-center px-6">
      <div className="max-w-lg w-full rounded-2xl bg-white border border-cvc-border p-10 shadow-sm text-center" data-testid="payment-status-card">
        {paid && (
          <>
            <CheckCircle size={56} weight="duotone" className="mx-auto text-scaled-green" />
            <h1 className="font-heading text-4xl text-cvc-navy mt-4">{paidCopy().heading}</h1>
            <p className="font-body text-cvc-ink-soft mt-3">{paidCopy().body}</p>
          </>
        )}
        {isCancelled && (
          <>
            <XCircle size={56} weight="duotone" className="mx-auto text-cvc-red" />
            <h1 className="font-heading text-4xl text-cvc-navy mt-4">Payment cancelled.</h1>
            <p className="font-body text-cvc-ink-soft mt-3">No charge was made. You can retry from the link Jonathan sent you, or reach out at 435-535-7000.</p>
          </>
        )}
        {!paid && !isCancelled && !isError && (
          <>
            <ArrowClockwise size={56} weight="duotone" className="mx-auto text-cvc-navy animate-spin" />
            <h1 className="font-heading text-4xl text-cvc-navy mt-4">Confirming your payment…</h1>
            <p className="font-body text-cvc-ink-soft mt-3">Checking with Stripe (attempt {attempts + 1}/10).</p>
          </>
        )}
        {isError && (
          <>
            <XCircle size={56} weight="duotone" className="mx-auto text-cvc-red" />
            <h1 className="font-heading text-4xl text-cvc-navy mt-4">We couldn't confirm.</h1>
            <p className="font-body text-cvc-ink-soft mt-3">
              {state.status === "timeout" && "Payment status check timed out. If you completed payment, you'll receive a confirmation email shortly."}
              {state.status === "expired" && "This checkout session has expired."}
              {state.status === "error" && (state.error || "Something went wrong checking the status.")}
            </p>
          </>
        )}
        <Link to="/" className="inline-block mt-8 font-body text-sm text-cvc-red underline underline-offset-4">← Back to Cache Valley Consulting</Link>
      </div>
    </div>
  );
}
